import { Layers, Paperclip, Plus } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { Sheet, SheetRow } from './Sheet';

type Item = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
};

type Props = {
  disabled?: boolean;
  onAttach?: () => void;
  onOpenSkills?: () => void;
};

/**
 * The `+` menu contains only actions available in this workspace.
 */
export function PlusMenu({ disabled, onAttach, onOpenSkills }: Props) {
  const [open, setOpen] = useState(false);
  const isPhone = !useMediaQuery('(min-width: 768px)');
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback((): void => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open || isPhone) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, isPhone]);

  const actions: Item[] = [
    {
      icon: <Paperclip size={20} strokeWidth={1.8} />,
      label: 'Файлы и фото',
      onClick: () => {
        setOpen(false);
        onAttach?.();
      },
    },
    {
      icon: <Layers size={20} strokeWidth={1.8} />,
      label: 'Навыки',
      onClick: () => {
        setOpen(false);
        onOpenSkills?.();
      },
    },
  ];

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Вложения и инструменты"
        className="mc-press flex h-11 w-11 items-center justify-center rounded-full text-foreground active:bg-subtle md:hover:bg-subtle disabled:cursor-not-allowed"
        title="Вложения и инструменты"
      >
        <Plus size={22} strokeWidth={1.8} aria-hidden="true" />
      </button>

      {isPhone && (
        <Sheet open={open} onClose={() => setOpen(false)} title="Добавить">
          <div className="space-y-1">
            {actions.map((action) => (
              <SheetRow
                key={action.label}
                icon={action.icon}
                label={action.label}
                onClick={action.onClick}
              />
            ))}
          </div>
        </Sheet>
      )}
      {open && !isPhone && (
        <div
          role="menu"
          aria-label="Вложения и инструменты"
          className="mc-menu-enter origin-bottom-left absolute bottom-[calc(100%+8px)] left-0 z-20 w-[min(calc(100vw-2rem),260px)] rounded-2xl border border-border/70 bg-surface p-1.5 text-[14px] mc-float"
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              onClick={action.onClick}
              className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-subtle"
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
